import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  Card,
  Row,
  Col,
  Select,
  InputNumber,
  Spin,
  message,
  Alert,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardLayout from "../LayoutNavbar/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../LayoutNavbar/Navbars/DashboardNavbar";
import usePerformanceReview from "Hooks/EmployeeHooks/usePerformanceReview";
import PerformanceReviewService from "Services/EmployeeServices/PerformanceReviewSerivce";
import moment from "moment";

const { TextArea } = Input;

// Shared card style for the form sections (cleaner than the old cramped boxes).
const sectionStyle = {
  border: "1px solid #e8e8e8",
  padding: "20px 24px",
  marginBottom: "16px",
  borderRadius: "10px",
  backgroundColor: "#ffffff",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
};

// Main section titles (I, II, III, IV, V) with a green accent underline.
const sectionTitleStyle = {
  fontSize: "18px",
  fontWeight: 600,
  color: "#1f2d3d",
  margin: "20px 0 12px",
  paddingBottom: "8px",
  borderBottom: "2px solid #b7eb8f",
};

// Sub-section headings (the individual performance factors).
const factorHeadingStyle = {
  fontSize: "15px",
  fontWeight: 600,
  color: "#1f2d3d",
  marginBottom: "6px",
};

const PerformanceReviewForm = () => {
  const { state } = useLocation();
  const navigate = useNavigate();

  const employeeId = state?.employeeId;

  // Use the custom hook
  const {
    employee,
    submitPerformanceReview,
    loading: hookLoading, // Rename to avoid conflict
  } = usePerformanceReview(employeeId);

  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [lowRatingAlert, setLowRatingAlert] = useState(false);
  const [skills, setSkills] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState({});
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [error, setError] = useState(null);
  const [weightWarning, setWeightWarning] = useState(null);
  const [missingFields, setMissingFields] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showMissingFieldsAlert, setShowMissingFieldsAlert] = useState(false);
  const isLoading = loading || hookLoading;

  const [headerData, setHeaderData] = useState({
    employeeId: "Loading...",
    employeeName: "Loading...",
    departmentName: "Loading...",

    reviewerName: "Loading...",
    designation: "Not specified",
    performancePeriod: "Not specified",
    hireDate: null,
  });

  // Rating options - simple numbers only
  const ratingOptions = [
    { value: 1, label: "1" },
    { value: 2, label: "2" },
    { value: 3, label: "3" },
    { value: 4, label: "4" },
    { value: 5, label: "5" },
  ];

  /** --- UTILITY FUNCTIONS --- **/

  const validateWeights = (accountabilities) => {
    const totalWeight = accountabilities
      .slice(0, 5)
      .reduce((sum, item) => sum + (Number(item?.weight) || 0), 0);
    if (totalWeight > 100) {
      setWeightWarning({
        type: "error",
        message: `Total weights cannot exceed 100% (Current: ${totalWeight}%)`,
      });
      return false;
    }
    if (totalWeight < 100 && accountabilities.length >= 5) {
      setWeightWarning({
        type: "warning",
        message: `Total weights should equal 100% (Current: ${totalWeight}%). Please adjust the weights.`,
      });
      return false;
    }
    if (totalWeight === 100) {
      setWeightWarning(null);
      return true;
    }
    return false;
  };

  const calculateOverallRating = () => {
    const accountabilities = form.getFieldValue("accountabilities") || [];
    validateWeights(accountabilities);
    const validAccountabilities = accountabilities
      .slice(0, 5)
      .filter((item) => item?.rating !== undefined && item?.weight !== undefined);
    if (validAccountabilities.length === 0) {
      form.setFieldsValue({ overallAccountabilityRating: 0 });
      setRefreshKey((prev) => prev + 1);
      return;
    }
    const totalScore = validAccountabilities.reduce((sum, item) => {
      const weight = Number(item.weight) || 0;
      const rating = Number(item.rating) || 0;
      return sum + rating * weight;
    }, 0);
    const totalWeight = validAccountabilities.reduce((sum, item) => {
      return sum + (Number(item.weight) || 0);
    }, 0);
    const overallRating = totalWeight > 0 ? totalScore / totalWeight : 0;
    form.setFieldsValue({
      overallAccountabilityRating: parseFloat(overallRating.toFixed(2)),
      summaryAccountabilityRating: parseFloat((overallRating * 10).toFixed(2)),
    });
    const hasLowScore = accountabilities.slice(0, 5).some((item) => Number(item?.rating) <= 3);
    setLowRatingAlert(hasLowScore);
    calculatePerformanceSummary();
  };

  const weightValidationRules = (index) => [
    { required: true, message: "Enter weight" },
    {
      validator: (_, value) => {
        const accountabilities = form.getFieldValue("accountabilities") || [];
        const currentValues = [...accountabilities];
        if (currentValues[index]) {
          currentValues[index].weight = value;
        }
        const totalWeight = currentValues
          .slice(0, 5)
          .reduce((sum, item) => sum + (Number(item?.weight) || 0), 0);
        if (totalWeight > 100) {
          return Promise.reject("Total weights cannot exceed 100%");
        }
        if (index === 4 && totalWeight < 100) {
          return Promise.reject("Total weights should equal 100%");
        }
        return Promise.resolve();
      },
    },
  ];

  const calculatePerformanceFactorsRating = () => {
    const performanceFactors = [
      form.getFieldValue("jobKnowledgeTechnicalSkillsRating"),
      form.getFieldValue("administrationRating"),
      form.getFieldValue("decisionMakingRating"),
      form.getFieldValue("communicationRating"),
      form.getFieldValue("initiativeResponsibilityRating"),
      form.getFieldValue("workRelationshipsRating"),
      form.getFieldValue("flexibilityRating"),
      form.getFieldValue("leadershipRating"),
      form.getFieldValue("freeThinkingRating"),
    ].filter((rating) => rating !== undefined && rating !== null);
    if (performanceFactors.length === 0) return;
    const sum = performanceFactors.reduce((total, rating) => total + rating, 0);
    const average = sum / performanceFactors.length;
    form.setFieldsValue({
      overallPerformanceFactorRating: parseFloat(average.toFixed(2)),
      summaryPerformanceFactorRating: parseFloat((average * 8).toFixed(2)),
    });
    calculatePerformanceSummary();
  };

  const calculateDynamicOrgRating = () => {
    const dynamicOrgFactors = [
      form.getFieldValue("demonstratingBehaviorsRating"),
      form.getFieldValue("valuingDiversityRating"),
    ].filter((rating) => rating !== undefined && rating !== null);
    if (dynamicOrgFactors.length === 0) return;
    const sum = dynamicOrgFactors.reduce((total, rating) => total + rating, 0);
    const average = sum / dynamicOrgFactors.length;
    form.setFieldsValue({
      overallDynamicOrgRating: parseFloat(average.toFixed(2)),
      summaryDynamicOrgRating: parseFloat((average * 2).toFixed(2)),
    });
    calculatePerformanceSummary();
  };

  const calculatePerformanceSummary = () => {
    const accountabilityRating = form.getFieldValue("summaryAccountabilityRating") || 0;
    const performanceFactorRating = form.getFieldValue("summaryPerformanceFactorRating") || 0;
    const dynamicOrgRating = form.getFieldValue("summaryDynamicOrgRating") || 0;
    const totalPerformanceRating =
      accountabilityRating + performanceFactorRating + dynamicOrgRating;
    form.setFieldsValue({
      totalPerformanceRating: parseFloat(totalPerformanceRating.toFixed(2)),
    });
  };

  const fetchSkills = async () => {
    try {
      setLoadingSkills(true);
      if (!employeeId) {
        throw new Error("Missing employee ID");
      }
      const skillsData = await PerformanceReviewService.getSkillsByEmployee(employeeId);
      setSkills(skillsData || []);
    } catch (error) {
      console.error("Failed to fetch skills:", error);
      message.error(`Failed to load skills: ${error.message}`);
      setError("Failed to load skills data");
    } finally {
      setLoadingSkills(false);
    }
  };
  // In your useEffect
  useEffect(() => {
    if (!employeeId) {
      setError("Missing employee ID");
      setLoading(false);
      return;
    }

    const fetchPerformanceData = async () => {
      try {
        setLoading(true); //  Now setLoading is defined
        setError(null);

        // Fetch skills and employee data
        await fetchSkills();

        // Set header data from employee info
        if (employee) {
          setHeaderData({
            employeeId: employee.employeeId,
            employeeName: `${employee.firstName} ${employee.lastName}`,
            departmentName: employee.departmentName,

            reviewerName: `${employee.supervisorFirstName} ${employee.supervisorLastName}`,
            designation: "Employee",
            performancePeriod:
              employee.periodStart && employee.periodEnd
                ? `${new Date(employee.periodStart).toLocaleDateString()} - ${new Date(
                    employee.periodEnd
                  ).toLocaleDateString()}`
                : "N/A",
            hireDate: employee.hireDate,
          });

          form.setFieldsValue({
            employeeId: employee.employeeId,
            employeeName: `${employee.firstName} ${employee.lastName}`,
            departmentName: employee.departmentName,
          });
        }
      } catch (error) {
        console.error("Failed to fetch performance data:", error);
        setError(error.message || "Failed to load performance data");
        message.error("Failed to load performance review data");
      } finally {
        setLoading(false); //  Now setLoading is defined
      }
    };

    fetchPerformanceData();
  }, [employeeId, employee]);

  const handleSkillChange = (value, index) => {
    setSelectedSkills((prev) => {
      const newSelected = { ...prev };
      Object.keys(newSelected).forEach((key) => {
        if (newSelected[key] === index) {
          delete newSelected[key];
        }
      });
      if (value) {
        newSelected[value] = index;
      }
      return newSelected;
    });
  };

  const validateBeforeSubmit = async () => {
    try {
      await form.validateFields();
      form.submit();
    } catch (error) {
      const errorFields = error.errorFields.map((field) => ({
        name: field.name.join("."),
        errors: field.errors,
      }));
      const missingFieldNames = [...new Set(errorFields.map((f) => f.name))];
      setMissingFields(missingFieldNames);
      setShowMissingFieldsAlert(true);
      if (errorFields.length > 0) {
        const firstErrorField = document.querySelector(`[name="${errorFields[0].name}"]`);
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    }
  };

  const onFinish = async (values) => {
    try {
      setSubmitting(true);
      setShowMissingFieldsAlert(false);

      // Prepare payload for submission
      const payload = {
        employeeId: parseInt(employeeId, 10),
        managerId: 1,

        accountabilityGoals:
          values.accountabilities?.map((goal, index) => ({
            goalNumber: index + 1,
            description: goal.goal,
            weight: goal.weight,
            rating: goal.rating,
            skillId: goal.skillId ?? null,
            summaryOfResults: goal.summary,
          })) || [],

        identifyAdditionalGoals: values.identifyAdditionalGoals || "",

        performanceFactors: {
          jobKnowledgeTechnicalSkillsRating: values.jobKnowledgeTechnicalSkillsRating,
          jobKnowledgeTechnicalSkillsEvidence: values.jobKnowledgeTechnicalSkillsEvidence,
          administrationRating: values.administrationRating,
          administrationEvidence: values.administrationEvidence,
          decisionMakingRating: values.decisionMakingRating,
          decisionMakingEvidence: values.decisionMakingEvidence,
          communicationRating: values.communicationRating,
          communicationEvidence: values.communicationEvidence,
          initiativeResponsibilityRating: values.initiativeResponsibilityRating,
          initiativeResponsibilityEvidence: values.initiativeResponsibilityEvidence,
          workRelationshipsRating: values.workRelationshipsRating,
          workRelationshipsEvidence: values.workRelationshipsEvidence,
          flexibilityRating: values.flexibilityRating,
          flexibilityEvidence: values.flexibilityEvidence,
          leadershipRating: values.leadershipRating || 0,
          leadershipEvidence: values.leadershipEvidence || "",
          freeThinkingRating: values.freeThinkingRating || 0,
          freeThinkingEvidence: values.freeThinkingEvidence || "",
        },

        dynamicOrganization: {
          demonstratingBehaviorsRating: values.demonstratingBehaviorsRating,
          valuingDiversityRating: values.valuingDiversityRating,
          examplesOfBehaviors: values.dynamicOrgExamples || "",
        },

        strengths: values.strengths || "",
        developmentNeeds: values.developmentNeeds || "",
        actionPlans: values.actionPlans || [],
        employeeComments: values.employeeComments || "",
      };

      // Use custom hook to submit
      const result = await submitPerformanceReview(payload);

      // Navigate back to check reviews page
      navigate("/employeedashboard/performance-review");
    } catch (error) {
      console.error("Submission error:", error);

      // Only show error if it's a genuine error (not 200 OK)
      if (error.response && error.response.status !== 200) {
        let errorMessage = "Failed to submit performance review";

        if (error.response?.data) {
          const serverError = error.response.data;

          if (typeof serverError === "string") {
            errorMessage = serverError;
          } else if (serverError.message) {
            errorMessage = serverError.message;
          } else if (serverError.title) {
            errorMessage = serverError.title;
          } else if (serverError.errors) {
            const validationErrors = Object.values(serverError.errors).flat();
            errorMessage = `Validation errors: ${validationErrors.join(", ")}`;
          }
        }

        message.error(String(errorMessage));
        setError(String(errorMessage));
      } else {
        // If it's a 200 response but caught as error, treat as success
        navigate("/employeedashboard/performance-review");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Loading and error states
  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <Spin size="large" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <div style={{ padding: "20px" }}>
          <Card>
            <Alert
              type="error"
              message="Error Loading Performance Review"
              description={error}
              showIcon
            />
            <Button
              className="btn-primary"
              onClick={() => window.location.reload()}
              style={{ marginTop: 16 }}
            >
              Try Again
            </Button>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div style={{ padding: "20px" }}>
        <Card
          className="card-professional"
          title={`Employee Performance Review - ${employee?.firstName} ${employee?.lastName}`}
          extra={
            <Button type="default" onClick={() => navigate(-1)}>
              Back
            </Button>
          }
        >
          <Form layout="vertical" onFinish={onFinish} form={form}>
            {/* Header Information */}
            <div
              style={sectionStyle}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <p style={{ marginBottom: 8 }}>
                    <strong>Employee ID:</strong> {headerData.employeeId}
                  </p>
                  <p style={{ marginBottom: 8 }}>
                    <strong>Employee Name:</strong> {headerData.employeeName}
                  </p>
                  <p style={{ marginBottom: 0 }}>
                    <strong>Department:</strong> {headerData.departmentName}
                  </p>
                </Col>
                <Col span={12}>
                  <p style={{ marginBottom: 8 }}>
                    <strong>Supervisor:</strong> {headerData.reviewerName}
                  </p>

                  <div style={{ display: "flex", alignItems: "center" }}>
                    <strong style={{ marginRight: 8, width: 80 }}>Hire Date:</strong>
                    <span>
                      {headerData.hireDate
                        ? moment(headerData.hireDate).format("MMM-DD-yyyy")
                        : "Not specified"}
                    </span>
                  </div>
                </Col>
              </Row>
            </div>

            {/* Keep these hidden fields for form submission */}
            <Form.Item
              name="employeeId"
              hidden
              initialValue={
                headerData.employeeId !== "Loading..." ? headerData.employeeId : employeeId
              }
            >
              <Input />
            </Form.Item>
            <Form.Item name="managerId" hidden initialValue={1}>
              <Input />
            </Form.Item>

            {/* Rating Codes Section */}
            <div
              style={sectionStyle}
            >
              <h5> Rating Codes</h5>
              <ul style={{ paddingLeft: 20 }}>
                <li>
                  {" "}
                  <p>
                    <strong>FE</strong> Far Exceeds Expectations. These associates far exceed their
                    accountabilities/goals. They are truly exceptions to the rule. (90 to 100 -- A
                    Grade)
                  </p>{" "}
                </li>
                <li>
                  {" "}
                  <p>
                    <strong>EE</strong> Exceeds Expectations. These associates do their jobs with
                    excellence and go far above the job expectations. (80 to 89 -- B grade)
                  </p>
                </li>
                <li>
                  {" "}
                  <p>
                    <strong>ME</strong> Meets Expectations. These associates do their jobs very
                    well- this is whats expected.( 70 to 79 -- C Grade)
                  </p>
                </li>
                <li>
                  {" "}
                  <p>
                    <strong>PM</strong> Partially Meets Expectations. These associates have
                    demonstrated consistent performance shortcomings and need to complete an action
                    plan for immediate performance improvement. (50 to 59 -- D Grade)
                  </p>
                </li>
                <li>
                  {" "}
                  <p>
                    <strong>FM</strong> Fails to Meet Expectations. These associates have not met
                    any of their job expectations. (Below 50 -- F Grade)
                  </p>
                </li>
              </ul>
            </div>

            {/* I. Accountabilities/Goals */}
            <h3 style={sectionTitleStyle}>I. Accountabilities/Goals</h3>
            <p>Identify major results achieved or not achieved during evaluation period.</p>
            {weightWarning && (
              <Alert
                type={weightWarning.type}
                message={weightWarning.message}
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            {[...Array(5)].map((_, index) => (
              <div
                key={index}
                style={sectionStyle}
              >
                <h6 style={{ marginBottom: "16px", fontWeight: 500 }}>
                  Accountability/Goal No. {index + 1}
                </h6>
                <Form.Item
                  name={["accountabilities", index, "skillId"]}
                  label="Related Skill"
                  rules={[{ required: true, message: "Please select a skill" }]}
                >
                  <Select
                    placeholder="Select a skill"
                    loading={loadingSkills}
                    onChange={(value) => handleSkillChange(value, index)}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {skills.map((skill) => (
                      <Select.Option
                        key={skill.skillId}
                        value={skill.skillId}
                        disabled={
                          selectedSkills[skill.skillId] !== undefined &&
                          selectedSkills[skill.skillId] !== index
                        }
                      >
                        {skill.skillName}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      name={["accountabilities", index, "weight"]}
                      label="% Weight"
                      rules={weightValidationRules(index)}
                    >
                      <InputNumber
                        min={0}
                        max={100}
                        style={{ width: "100%" }}
                        placeholder="Enter only digits"
                        parser={(value) => value.replace(/\D/g, "")}
                        onKeyPress={(e) => {
                          if (!/[0-9]/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onChange={() => {
                          calculateOverallRating();
                          validateWeights(form.getFieldValue("accountabilities") || []);
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name={["accountabilities", index, "rating"]}
                      label="Rating"
                      rules={[{ required: true, message: "Enter rating" }]}
                    >
                      <Select placeholder="Select rating" onChange={calculateOverallRating}>
                        {ratingOptions.map((option) => (
                          <Select.Option key={option.value} value={option.value}>
                            {option.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item
                  name={["accountabilities", index, "goal"]}
                  label="Accountability/Goal"
                  rules={[{ required: true, message: "Enter goal" }]}
                >
                  <TextArea rows={2} />
                </Form.Item>
                <Form.Item
                  name={["accountabilities", index, "summary"]}
                  label="Summary of Results"
                  rules={[{ required: true, message: "Please enter summary of result" }]}
                >
                  <TextArea rows={2} />
                </Form.Item>
              </div>
            ))}

            <Form.Item
              name="overallAccountabilityRating"
              label="Overall Accountability/Goal Rating"
            >
              <InputNumber
                key={`rating-${refreshKey}`}
                min={0}
                max={100}
                style={{ width: "100%" }}
                disabled
                precision={2}
                formatter={(value) => value ?? "0.00"}
              />
            </Form.Item>
            <Form.Item
              name="identifyAdditionalGoals"
              label="Identify Significant Revisions or Additions to Accountabilities/Goals"
              rules={[{ required: true, message: "Please enter your comment" }]}
            >
              <TextArea rows={2} />
            </Form.Item>
            {/* II. Performance Factors */}
            <h3 style={sectionTitleStyle}>II. Performance Factors</h3>
            <p>
              Evaluate the associate s performance in each of the following factors. Support
              comments may be indicated for each factor.
            </p>
            <div
              style={sectionStyle}
            >
              <h6 style={factorHeadingStyle}>
                <strong>Job Knowledge/Technical Skills:</strong>
              </h6>
              <p>
                Demonstrates application of technical and procedural knowledge and expertise;
                Possesses knowledge of company and departmental policies and procedures.
              </p>
              <Form.Item
                name="jobKnowledgeTechnicalSkillsRating"
                label="Rating"
                rules={[{ required: true, message: "Please select a rating" }]}
              >
                <Select placeholder="Select rating" onChange={calculatePerformanceFactorsRating}>
                  {ratingOptions.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="jobKnowledgeTechnicalSkillsEvidence"
                label="As Evidenced By:"
                rules={[{ required: true, message: "Please enter your Evidence " }]}
              >
                <TextArea rows={2} />
              </Form.Item>
            </div>
            <div
              style={sectionStyle}
            >
              <h6 style={factorHeadingStyle}>
                <strong>Administration:</strong>
              </h6>
              <p>
                {" "}
                Ability to plan and organize, establish priorities, schedule time and resources to
                accomplish tasks; Establishes appropriate controls to assure the monitoring and
                evaluation of activities and results.
              </p>
              <Form.Item
                name="administrationRating"
                label="Rating"
                rules={[{ required: true, message: "Please select a rating" }]}
              >
                <Select placeholder="Select rating" onChange={calculatePerformanceFactorsRating}>
                  {ratingOptions.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="administrationEvidence"
                label="As Evidenced By:"
                rules={[{ required: true, message: "Please enter your evidence" }]}
              >
                <TextArea rows={2} />
              </Form.Item>
            </div>
            <div
              style={sectionStyle}
            >
              <h6 style={factorHeadingStyle}>
                <strong>Decision-Making:</strong>
              </h6>
              <p>
                Ability to make timely/effective decisions using logic, analysis, and evaluative
                skills; Identifies and evaluates all information that has important impact for
                making the decision at hand.
              </p>
              <Form.Item
                name="decisionMakingRating"
                label="Rating "
                rules={[{ required: true, message: "Please select a rating" }]}
              >
                <Select placeholder="Select rating" onChange={calculatePerformanceFactorsRating}>
                  {ratingOptions.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="decisionMakingEvidence"
                label="As Evidenced By:"
                rules={[{ required: true, message: "Please enter your evidence " }]}
              >
                <TextArea rows={2} />
              </Form.Item>
            </div>
            <div
              style={sectionStyle}
            >
              <h6 style={factorHeadingStyle}>
                <strong>Communication:</strong>
              </h6>
              <p>
                Ability to listen effectively and respond clearly and directly; Prepare
                clear/concise reports; effectively give or explain instructions and ideas to others;
                Functions effectively in one-to-one meetings, group meetings and oral presentations.
              </p>
              <Form.Item
                name="communicationRating"
                label=" Rating"
                rules={[{ required: true, message: "Please select a rating" }]}
              >
                <Select placeholder="Select rating" onChange={calculatePerformanceFactorsRating}>
                  {ratingOptions.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="communicationEvidence"
                label="As Evidenced By:"
                rules={[{ required: true, message: "Please enter your evidence" }]}
              >
                <TextArea rows={2} />
              </Form.Item>
            </div>
            <div
              style={sectionStyle}
            >
              <h6 style={factorHeadingStyle}>
                <strong>Initiative/Responsibility:</strong>
              </h6>
              <p>
                {" "}
                Ability to work independently, find new solutions, expand on existing ideas and
                suggest assignments beyond managers expectations/requests.
              </p>
              <Form.Item
                name="initiativeResponsibilityRating"
                label="Rating"
                rules={[{ required: true, message: "Please select a rating" }]}
              >
                <Select placeholder="Select rating" onChange={calculatePerformanceFactorsRating}>
                  {ratingOptions.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="initiativeResponsibilityEvidence"
                label="As Evidenced By:"
                rules={[{ required: true, message: "Please enter your evidence " }]}
              >
                <TextArea rows={2} />
              </Form.Item>
            </div>
            <div
              style={sectionStyle}
            >
              <h6 style={factorHeadingStyle}>
                <strong>Work Relationships:</strong>
              </h6>
              <p>
                {" "}
                Level of quality of interactions with others to accomplish objectives and
                contributes to their objectives.
              </p>
              <Form.Item
                name="workRelationshipsRating"
                label="Rating"
                rules={[{ required: true, message: "Please select a rating" }]}
              >
                <Select placeholder="Select rating" onChange={calculatePerformanceFactorsRating}>
                  {ratingOptions.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="workRelationshipsEvidence"
                label="As Evidenced By:"
                rules={[{ required: true, message: "Please enter your evidence" }]}
              >
                <TextArea rows={2} />
              </Form.Item>
            </div>
            <div
              style={sectionStyle}
            >
              <h6 style={factorHeadingStyle}>
                <strong>Flexibility:</strong>
              </h6>
              <p>
                {" "}
                Ability to adjust to changes in job conditions, assignments, schedules, and to
                change or vary approach or strategy to accomplish desired results.
              </p>
              <Form.Item
                name="flexibilityRating"
                label="Rating"
                rules={[{ required: true, message: "Please select a rating" }]}
              >
                <Select placeholder="Select rating" onChange={calculatePerformanceFactorsRating}>
                  {ratingOptions.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="flexibilityEvidence"
                label="As Evidenced By:"
                rules={[{ required: true, message: "Please enter your evidence" }]}
              >
                <TextArea rows={2} />
              </Form.Item>
            </div>
            <div
              style={sectionStyle}
            >
              <h6 style={factorHeadingStyle}>
                <strong>Leadership:</strong>
              </h6>
              <p>Demonstrates ability to lead, motivate and guide others when appropriate.</p>
              <Form.Item
                name="leadershipRating"
                label="Rating"
                rules={[{ required: true, message: "Please select a rating" }]}
              >
                <Select placeholder="Select rating" onChange={calculatePerformanceFactorsRating}>
                  {ratingOptions.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="leadershipEvidence"
                label="As Evidenced By:"
                rules={[{ required: true, message: "Please enter your evidence" }]}
              >
                <TextArea rows={2} />
              </Form.Item>
            </div>
            <div
              style={sectionStyle}
            >
              <h6 style={factorHeadingStyle}>
                <strong>Free Thinking/Creativity:</strong>
              </h6>
              <p>Demonstrates innovative thinking and creative problem-solving.</p>
              <Form.Item
                name="freeThinkingRating"
                label="Rating"
                rules={[{ required: true, message: "Please select a rating" }]}
              >
                <Select placeholder="Select rating" onChange={calculatePerformanceFactorsRating}>
                  {ratingOptions.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="freeThinkingEvidence"
                label="As Evidenced By:"
                rules={[{ required: true, message: "Please enter your evidence " }]}
              >
                <TextArea rows={2} />
              </Form.Item>
            </div>
            <div
              style={sectionStyle}
            >
              <h6 style={factorHeadingStyle}>
                <strong>Overall Performance Factor Rating</strong>
              </h6>
              <Form.Item
                name="overallPerformanceFactorRating"
                extra="Average of all 9 performance factor ratings"
              >
                <InputNumber min={0} max={100} style={{ width: "100%" }} disabled precision={2} />
              </Form.Item>
            </div>
            {/* III. Driving the Dynamic Organization */}
            <h3 style={sectionTitleStyle}>III. Driving the Dynamic Organization</h3>
            <div
              style={sectionStyle}
            >
              <h6 style={factorHeadingStyle}>
                <strong>Demonstrating Behaviors:</strong>
              </h6>
              <p>
                {" "}
                Consistently displays the values and behaviors of the Dynamic Organization by
                visualizing the ideal customer experience; Exhibits passion about HT Inc and their
                job; Demonstrates a willingness to learn; Displays flexibility in our ever-changing
                environment; Conveys respect for each other and shows confidence in HT Inc, its
                owner and each other.
              </p>
              <Form.Item
                name="demonstratingBehaviorsRating"
                label="Rating"
                rules={[{ required: true, message: "Please select a rating" }]}
              >
                <Select placeholder="Select rating" onChange={calculateDynamicOrgRating}>
                  {ratingOptions.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
            <div
              style={sectionStyle}
            >
              <h6 style={factorHeadingStyle}>
                <strong>Valuing Diversity:</strong>
              </h6>
              <p>
                {" "}
                Recognizes and values the diversity of our associates to further the goals and
                objectives of our company.
              </p>
              <Form.Item
                name="valuingDiversityRating"
                label="Rating"
                rules={[{ required: true, message: "Please select a rating" }]}
              >
                <Select placeholder="Select rating" onChange={calculateDynamicOrgRating}>
                  {ratingOptions.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
            <div
              style={sectionStyle}
            >
              <Form.Item
                name="dynamicOrgExamples"
                label="Examples of actions that support the Behaviors of the Dynamic Organization:"
                rules={[{ required: true, message: "Please enter example of actions" }]}
              >
                <TextArea rows={2} />
              </Form.Item>
            </div>
            <div
              style={sectionStyle}
            >
              <Form.Item
                name="overallDynamicOrgRating"
                label="Overall Dynamic Organization Rating"
                extra="Average of the 2 dynamic organization ratings"
              >
                <InputNumber min={0} max={100} style={{ width: "100%" }} disabled precision={2} />
              </Form.Item>
            </div>
            {/* IV. Performance Evaluation Summary */}
            <div
              style={sectionStyle}
            >
              <h3 style={sectionTitleStyle}>IV. Performance Evaluation Summary</h3>
              <table style={{ width: "100%", marginBottom: 20, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#fafafa" }}>
                    <th style={{ border: "1px solid #eee", padding: "10px 12px" }}>Section</th>
                    <th style={{ border: "1px solid #eee", padding: "10px 12px" }}>Section Description</th>
                    <th style={{ border: "1px solid #eee", padding: "10px 12px" }}>
                      Percentage of Total Rating
                    </th>
                    <th style={{ border: "1px solid #eee", padding: "10px 12px" }}>Score</th>
                    <th style={{ border: "1px solid #eee", padding: "10px 12px" }}>Rating Code</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ border: "1px solid #eee", padding: "10px 12px" }}>I</td>
                    <td style={{ border: "1px solid #eee", padding: "10px 12px" }}>
                      Overall Accountability/Goal Rating
                    </td>
                    <td style={{ border: "1px solid #eee", padding: "10px 12px" }}>50%</td>
                    <td style={{ border: "1px solid #eee", padding: "10px 12px" }}>
                      <Form.Item name="summaryAccountabilityRating" noStyle>
                        <InputNumber
                          min={0}
                          max={50}
                          style={{ width: "100%" }}
                          disabled
                          precision={2}
                        />
                      </Form.Item>
                    </td>
                    <td style={{ border: "1px solid #eee", padding: "10px 12px" }}>
                      {(() => {
                        const score = form.getFieldValue("summaryAccountabilityRating") || 0;
                        const percentage = score * 10;
                        if (percentage >= 90) return "FE";
                        if (percentage >= 80) return "EE";
                        if (percentage >= 70) return "ME";
                        if (percentage >= 50) return "PM";
                        return "FM";
                      })()}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #eee", padding: "10px 12px" }}>II</td>
                    <td style={{ border: "1px solid #eee", padding: "10px 12px" }}>
                      Overall Performance Factors Rating
                    </td>
                    <td style={{ border: "1px solid #eee", padding: "10px 12px" }}>40%</td>
                    <td style={{ border: "1px solid #eee", padding: "10px 12px" }}>
                      <Form.Item name="summaryPerformanceFactorRating" noStyle>
                        <InputNumber
                          min={0}
                          max={40}
                          style={{ width: "100%" }}
                          disabled
                          precision={2}
                        />
                      </Form.Item>
                    </td>
                    <td style={{ border: "1px solid #eee", padding: "10px 12px" }}>
                      {(() => {
                        const score = form.getFieldValue("summaryPerformanceFactorRating") || 0;
                        const percentage = score * 8;

                        if (percentage >= 90) return "FE";
                        if (percentage >= 80) return "EE";
                        if (percentage >= 70) return "ME";
                        if (percentage >= 50) return "PM";
                        return "FM";
                      })()}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #eee", padding: "10px 12px" }}>III</td>
                    <td style={{ border: "1px solid #eee", padding: "10px 12px" }}>
                      Overall Dynamic Organization Rating
                    </td>
                    <td style={{ border: "1px solid #eee", padding: "10px 12px" }}>10%</td>
                    <td style={{ border: "1px solid #eee", padding: "10px 12px" }}>
                      <Form.Item name="summaryDynamicOrgRating" noStyle>
                        <InputNumber
                          min={0}
                          max={10}
                          style={{ width: "100%" }}
                          disabled
                          precision={2}
                        />
                      </Form.Item>
                    </td>
                    <td style={{ border: "1px solid #eee", padding: "10px 12px" }}>
                      {(() => {
                        const score = form.getFieldValue("summaryDynamicOrgRating") || 0;
                        const percentage = score * 2;

                        if (percentage >= 90) return "FE";
                        if (percentage >= 80) return "EE";
                        if (percentage >= 70) return "ME";
                        if (percentage >= 50) return "PM";
                        return "FM";
                      })()}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #eee", padding: "10px 12px" }} colSpan="2">
                      TOTAL PERFORMANCE RATING
                    </td>
                    <td style={{ border: "1px solid #eee", padding: "10px 12px" }}>100%</td>
                    <td style={{ border: "1px solid #eee", padding: "10px 12px" }}>
                      <Form.Item name="totalPerformanceRating" noStyle>
                        <InputNumber
                          min={0}
                          max={100}
                          style={{ width: "100%", fontWeight: "bold" }}
                          disabled
                          precision={2}
                        />
                      </Form.Item>
                    </td>
                    <td style={{ border: "1px solid #eee", padding: "10px 12px", fontWeight: "bold" }}>
                      {(() => {
                        const totalScore = form.getFieldValue("totalPerformanceRating") || 0;

                        if (totalScore >= 90) return "FE (Far Exceeds)";
                        if (totalScore >= 80) return "EE (Exceeds)";
                        if (totalScore >= 70) return "ME (Meets)";
                        if (totalScore >= 50) return "PM (Partially Meets)";
                        return "FM (Fails to Meet)";
                      })()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {/* V. Summary of Strengths and Development Needs */}
            <div
              style={sectionStyle}
            >
              <h3 style={sectionTitleStyle}>V. Summary of Strengths and Development Needs</h3>
              <Form.Item
                name="strengths"
                label="A. Associate Strengths"
                style={{ marginBottom: 10, padding: 5 }}
                rules={[{ required: true, message: "Please select a rating" }]}
              >
                <TextArea rows={2} />
              </Form.Item>
              <Form.Item
                name="developmentNeeds"
                label="B. Associate Development Needs/Improvement Areas "
                style={{ marginBottom: 10, padding: 5 }}
                rules={[{ required: true, message: "Please select a rating" }]}
              >
                <TextArea rows={2} />
              </Form.Item>
              <Form.Item
                label="Action Plan(s) for Improvement"
                name="actionPlans"
                rules={[{ required: true, message: "Please select a rating" }]}
              >
                <Form.List name="actionPlans">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Row key={key} gutter={16} style={{ marginBottom: 8 }}>
                          <Col span={22}>
                            <Form.Item
                              {...restField}
                              name={name}
                              rules={[{ required: true, message: "Action item is required" }]}
                            >
                              <Input.TextArea rows={2} placeholder="Enter action item" />
                            </Form.Item>
                          </Col>
                          <Col span={2}>
                            <Button
                              type="text"
                              onClick={() => remove(name)}
                              icon={<DeleteOutlined />}
                              danger
                            />
                          </Col>
                        </Row>
                      ))}
                      <Form.Item>
                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                          Add Action Item
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>
              </Form.Item>
              <Form.Item
                name="employeeComments"
                label="D. Associate Comments"
                style={{ marginBottom: 10, padding: 5 }}
              >
                <TextArea
                  rows={2}
                  placeholder="(The associate may comment on any part of this evaluation)"
                />
              </Form.Item>
            </div>

            {/* Submit Button */}
            <Form.Item style={{ marginTop: 20 }}>
              {showMissingFieldsAlert && (
                <Alert
                  message="Missing Required Fields"
                  description={
                    <div>
                      Please complete the following required fields:
                      <ul style={{ marginTop: 8, marginBottom: 0 }}>
                        {missingFields.map((field, index) => (
                          <li key={index + 1}>{field}</li>
                        ))}
                      </ul>
                    </div>
                  }
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}
              {lowRatingAlert && (
                <Alert
                  message="Performance Alert"
                  description="One or more accountability goals have a rating of 3 or less. Please consider assigning additional training or improvement actions."
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}
              <Button className="btn-primary" onClick={validateBeforeSubmit} loading={submitting}>
                Submit Review
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default PerformanceReviewForm;
